/**
 * Everything that went wrong, in one list the screen can show. Nothing in the kiosk swallows an
 * error: it either recovers and says so here (level `warn`), or it cannot and says so here (`error`).
 */
import { createStore } from './store';

export type DiagnosticLevel = 'info' | 'warn' | 'error';

export interface Diagnostic {
  id: number;
  at: number;
  level: DiagnosticLevel;
  /** Who reports it: "transport.sse", "layout", "widget:seat-map", … */
  source: string;
  message: string;
  detail?: unknown;
}

const MAX_KEPT = 200;
let nextId = 1;

export const diagnosticsStore = createStore<Diagnostic[]>('diagnostics', []);

export function report(level: DiagnosticLevel, source: string, message: string, detail?: unknown): void {
  const entry: Diagnostic = { id: nextId++, at: Date.now(), level, source, message, detail };
  diagnosticsStore.set((list) => [...list, entry].slice(-MAX_KEPT));
  const line = `[${source}] ${message}`;
  if (level === 'error') console.error(line, detail ?? '');
  else if (level === 'warn') console.warn(line, detail ?? '');
  else console.info(line, detail ?? '');
}

/** Report a problem only the first time it happens (a missing translation, a refused hello). */
const seenOnce = new Set<string>();
export function reportOnce(level: DiagnosticLevel, source: string, message: string, detail?: unknown): void {
  const key = `${source}|${message}`;
  if (seenOnce.has(key)) return;
  seenOnce.add(key);
  report(level, source, message, detail);
}

export function describeError(e: unknown): string {
  if (e instanceof Error) return e.message;
  if (typeof e === 'string') return e;
  try {
    return JSON.stringify(e);
  } catch {
    return String(e);
  }
}
